import React from "react";
import { Avatar, AvatarGroup, AvatarProps, Badge, Skeleton, Stack, styled, Typography } from "@mui/material";
import { PropsWithLoadingState } from "../../../utilities";
import { useTranslation } from "react-i18next";
import { getRelativeDateString } from "../../../utilities/time";

export interface IChatInboxItemProps {
    /** The image representing the thread (can be a user avatar or a listing main picture) */
    image: AvatarProps;
    /** The optional sub image as badge of the main image (for guest/cohost) */
    avatars?: AvatarProps | [AvatarProps, AvatarProps];
    /** The top status of the converstation (depends on chat type) */
    status?: string;
    /** The title of the thread, usually the booking date and listing name */
    title: string;
    /** The sub-title of the thread, the cohost or guest name */
    subtitle?: string;
    /** The timestamp of the last posted message in the thread */
    timestamp?: Date;
    /** The description of the thread, could be the summary of the last message in the thread */
    description?: string;
    /** To know if the item has been selected (for styling) */
    isSelected?: boolean;
    /** To know if there is unread message in the thread (for styling) */
    unreadCount?: number;
    /** To know if the description should be italic for draft message */
    isDraft?: boolean;
    /** To know if the latest message is from the current user */
    isOwnLastMessage?: boolean;
    /** Callback press on the item */
    onClick: () => void;
}

const SmallAvatar = styled(Avatar)(({ theme }) => ({
    width: 22,
    height: 22,
    border: `2px solid ${theme.palette.background.paper}`,
}));

const MainAvatar = styled(Avatar)(({ theme }) => ({
    width: 66,
    height: 66,
}))

/** 
 * The item in the side bar. If clicked should open the full thread of that conversation.
 * @param props IChatInboxItemProps
 * @see ChatInbox
 */
export const ChatInboxItem = ({
    image,
    avatars,
    status,
    title,
    subtitle,
    timestamp,
    description,
    isSelected,
    unreadCount,
    isDraft,
    isOwnLastMessage,
    onClick,
    loading }: PropsWithLoadingState<IChatInboxItemProps>) => {
    const { t, i18n } = useTranslation();
    return (
        <Stack direction={'row'}
            justifyContent={'start'}
            alignItems={'center'}
            spacing={2}
            onClick={onClick}
            bgcolor={!!isSelected ? '#F7F7F7' : '#FFF'}
            p={1}
            // Needed for child typo noWrap
            minWidth={0}
        >
            {!!loading ?
                <Skeleton variant='rounded' height={66} width={66} />
                : !!avatars ?
                    <Badge
                        overlap="circular"
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        badgeContent={
                            Array.isArray(avatars) ?
                                <AvatarGroup>
                                    <SmallAvatar {...avatars[0]} />
                                    <SmallAvatar {...avatars[1]} />
                                </AvatarGroup>
                                : <SmallAvatar {...avatars} />
                        }
                    >
                        <MainAvatar variant="rounded" {...image} />
                    </Badge>
                    :
                    <MainAvatar variant="circular" {...image} />

            }
            <Stack direction={'column'}
                justifyContent={'center'}
                alignItems={'stretch'}
                width={'100%'}
                // Needed for child typo noWrap - CRITICAL!
                minWidth={0}
                overflow={'hidden'}
            >
                <Stack direction={'row'} justifyContent={'space-between'} spacing={1} minWidth={0}>
                    <Typography noWrap>{!loading ? status : <Skeleton width={30} />}</Typography>
                    <Typography flexShrink={0}>{!loading ? (!!timestamp ? getRelativeDateString(timestamp, i18n.language) : "") : <Skeleton width={20} />}</Typography>
                </Stack>
                <Typography noWrap fontWeight={'bold'}>{!loading ? title : <Skeleton width={'80%'} />}</Typography>
                <Typography noWrap>{!loading ? subtitle : <Skeleton width={'50%'} />}</Typography>
                {!!description &&
                    <Typography noWrap sx={{ fontStyle: isDraft ? 'italic' : 'inherit' }}>{
                        !loading ?
                            isDraft ? t('chat.inbox.draftMessage', { message: description })
                                : isOwnLastMessage ? t('chat.inbox.ownMessage', { message: description })
                                    : description
                            : <Skeleton width={'100%'} />}</Typography>}
            </Stack>
        </Stack>
    );
}